import { useState, useEffect } from "react";
import { Form, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";

const BASE = "https://dailydish.in";
const SALES_API = `${BASE}/api/admin/plan/sales-tracker`;
const PRODUCTS_API = `${BASE}/api/production/products`;
const LOG_API = `${BASE}/api/production/log`;

const SESSION_LABEL = { b: "Breakfast", l: "Lunch", d: "Dinner" };
const SESSION_COLOR = {
  b: { bg: "#FAEEDA", color: "#BA7517", border: "#EF9F27" },
  l: { bg: "#E1F5EE", color: "#085041", border: "#1D9E75" },
  d: { bg: "#E6F1FB", color: "#185FA5", border: "#185FA5" },
};
const TYPE_UNIT = { weight: "kg", pieces: "pcs", pack: "packs" };
const THRESH = { weight: 0.005, pieces: 0.5, pack: 0.5 };

function fmtQty(val, type) {
  if (val === null || val === undefined || val === "") return "—";
  const unit = TYPE_UNIT[type] || "";
  return type === "weight"
    ? `${parseFloat(val).toFixed(2)} ${unit}`
    : `${parseFloat(val)} ${unit}`;
}

function deriveStatus(target, log, type) {
  if (!log || log.status === "pending") return "pending";
  if (["open", "recook"].includes(log.status)) return log.status;
  const thresh = THRESH[type] || 0.5;
  const cooked = log.totalCooked || 0;
  if (!cooked) return "pending";
  const diff = cooked - target;
  if (Math.abs(diff) <= thresh) return "ready";
  if (diff < 0) return "short";
  return "excess";
}

const STATUS_STYLE = {
  pending: { bg: "#f0f0f0", color: "#666", label: "Pending" },
  open: { bg: "#E6F1FB", color: "#185FA5", label: "In Progress" },
  ready: { bg: "#E1F5EE", color: "#085041", label: "✓ Ready" },
  short: { bg: "#FCEBEB", color: "#A32D2D", label: "⚠ Shortage" },
  excess: { bg: "#FEF3E7", color: "#994010", label: "↑ Excess" },
  recook: { bg: "#FAEEDA", color: "#BA7517", label: "↻ Recooking" },
};

const RESULT_COLOR = { short: "#A32D2D", excess: "#994010", ready: "#085041" };
const RESULT_LABEL = {
  short: "⚠ Shortage",
  excess: "↑ Excess",
  ready: "✓ Ready",
};
const CARD_LEFT = {
  ready: "#1D9E75",
  short: "#E24B4A",
  excess: "#EF9F27",
  open: "#185FA5",
  recook: "#EF9F27",
  pending: "#e0e0e0",
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "3px 12px",
        borderRadius: 20,
        background: s.bg,
        color: s.color,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

function ItemCard({ entry, type }) {
  const ci = entry.cookableItem;
  const log = entry.log;
  const status = deriveStatus(entry.target, log, type);
  const cooked = log?.totalCooked || 0;
  const stillToCook = Math.max(
    0,
    Math.round((entry.target - cooked) * 1000) / 1000,
  );
  const batches = log?.batches || [];
  const hasBatches = batches.length > 0;
  const hasLegacyLog = log && log.status !== "pending" && !hasBatches;
  const borderLeft = `4px solid ${CARD_LEFT[status] || "#e0e0e0"}`;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #eee",
        borderLeft,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      {/* Summary row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 16,
          padding: "16px 20px",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
            {ci?.name || "—"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#aaa",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {log?.chefId?.name && <span>Chef: {log.chefId.name}</span>}
            {(log?.recookCount || 0) > 0 && (
              <span style={{ color: "#BA7517" }}>
                {log.recookCount} recook{log.recookCount > 1 ? "s" : ""}
              </span>
            )}
            {log?.readyTime && <span>Last batch: {log.readyTime}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: "#aaa",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Target
            </div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {fmtQty(entry.target, type)}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: "#aaa",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Cooked
            </div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: log ? "#1A1A18" : "#ccc",
              }}
            >
              {log ? fmtQty(cooked, type) : "—"}
            </div>
          </div>
          {["short", "open", "recook", "pending"].includes(status) &&
            stillToCook > 0 && (
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: 11,
                    color: "#A32D2D",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Still to Cook
                </div>
                <div
                  style={{ fontWeight: 700, fontSize: 15, color: "#E24B4A" }}
                >
                  {fmtQty(stillToCook, type)}
                </div>
              </div>
            )}
          <StatusBadge status={status} />
        </div>
      </div>

      {/* Batch timeline */}
      {(hasBatches || hasLegacyLog) && (
        <div style={{ borderTop: "1px solid #f5f5f5", background: "#fafafa" }}>
          <div
            style={{
              padding: "10px 20px 6px",
              fontSize: 11,
              fontWeight: 700,
              color: "#bbb",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Cooking Log
          </div>

          {/* New batch-tracked entries */}
          {hasBatches &&
            batches.map((b, bi) => {
              const rc = RESULT_COLOR[b.resultStatus] || "#888";
              const rl = RESULT_LABEL[b.resultStatus] || "";
              const isLast = bi === batches.length - 1;
              return (
                <div key={bi} style={{ position: "relative" }}>
                  {/* Vertical connector line */}
                  {!isLast && (
                    <div
                      style={{
                        position: "absolute",
                        left: 30,
                        top: 40,
                        bottom: -6,
                        width: 2,
                        background: "#e8e8e8",
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      padding: "10px 20px",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Batch bubble */}
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: b.batchNum === 1 ? "#E6F1FB" : "#FAEEDA",
                        color: b.batchNum === 1 ? "#185FA5" : "#BA7517",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                        marginTop: 1,
                        zIndex: 1,
                      }}
                    >
                      {b.batchNum}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          {b.batchNum === 1
                            ? "Initial batch"
                            : `Recook #${b.batchNum - 1}`}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>
                          {fmtQty(b.qty, type)}
                        </span>
                        {b.time && (
                          <span style={{ fontSize: 12, color: "#aaa" }}>
                            at {b.time}
                          </span>
                        )}
                        {b.chefId && (
                          <span style={{ fontSize: 12, color: "#888" }}>
                            {typeof b.chefId === "object" ? b.chefId.name : ""}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 6,
                        }}
                      >
                        {(b.images || []).map((img, j) => (
                          <img
                            key={j}
                            src={img}
                            alt=""
                            onClick={() => window.open(img, "_blank")}
                            style={{
                              width: 44,
                              height: 44,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #e8e8e8",
                              cursor: "pointer",
                            }}
                          />
                        ))}
                        {rl && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: rc,
                              marginLeft: (b.images || []).length ? 4 : 0,
                            }}
                          >
                            → {rl}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

          {/* Legacy fallback: log exists but no batches array (data before batch tracking) */}
          {hasLegacyLog && (
            <div
              style={{
                display: "flex",
                gap: 14,
                padding: "10px 20px 14px",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "#E6F1FB",
                  color: "#185FA5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                1
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Cooked</span>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>
                    {fmtQty(cooked, type)}
                  </span>
                  {log.readyTime && (
                    <span style={{ fontSize: 12, color: "#aaa" }}>
                      at {log.readyTime}
                    </span>
                  )}
                  {log.chefId?.name && (
                    <span style={{ fontSize: 12, color: "#888" }}>
                      {log.chefId.name}
                    </span>
                  )}
                </div>
                {(log.images || []).length > 0 && (
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {log.images.slice(0, 4).map((img, j) => (
                      <img
                        key={j}
                        src={img}
                        alt=""
                        onClick={() => window.open(img, "_blank")}
                        style={{
                          width: 44,
                          height: 44,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #e8e8e8",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 12,
                    color: "#bbb",
                    marginTop: 6,
                    fontStyle: "italic",
                  }}
                >
                  Detailed batch log not available for this entry
                </div>
              </div>
            </div>
          )}

          <div style={{ height: 4 }} />
        </div>
      )}
    </div>
  );
}

function SessionBlock({ date, session, products }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const sc = SESSION_COLOR[session];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [salesRes, logsRes] = await Promise.all([
          axios.get(SALES_API, {
            params: { date, session: SESSION_LABEL[session] },
          }),
          axios.get(LOG_API, { params: { date, session } }),
        ]);
        const salesData = salesRes.data.success ? salesRes.data.data : [];
        const logs = logsRes.data.success ? logsRes.data.data : [];

        // One sales row per (product, hub): precutoffConfirmed is per-hub so it sums,
        // but windowFixedQty is the product-level pool total repeated verbatim on every
        // hub row — take the max, never the sum, or an item ordered at N hubs inflates
        // its window qty N times. Same rule as AdminPlanDashboard and OrdersTab.
        const acc = new Map();
        salesData.forEach((item) => {
          const pre = item.precutoffConfirmed || 0;
          const win = item.windowFixedQty || 0;
          if (pre > 0 || win > 0) {
            const ex = acc.get(item.foodName);
            if (ex) {
              ex.pre += pre;
              ex.win = Math.max(ex.win, win);
            } else acc.set(item.foodName, { pre, win });
          }
        });

        const om = new Map();
        acc.forEach((v, name) => om.set(name, v.pre + v.win));

        const cookableTargets = new Map();
        products.forEach((p) => {
          const qty = om.get(p.name) || 0;
          if (!qty) return;
          p.components.forEach((c) => {
            const cid = c.cookableItemId?._id || c.cookableItemId;
            const ci = c.cookableItemId;
            const ex = cookableTargets.get(cid);
            if (ex) ex.target += qty * c.qty;
            else
              cookableTargets.set(cid, {
                cookableItem: ci,
                target: qty * c.qty,
              });
          });
        });

        const result = Array.from(cookableTargets.entries()).map(
          ([cid, { cookableItem, target }]) => {
            const log =
              logs.find(
                (l) => (l.cookableItemId?._id || l.cookableItemId) === cid,
              ) || null;
            return {
              cookableItem,
              target: Math.round(target * 1000) / 1000,
              log,
            };
          },
        );
        setEntries(result);
      } catch {
        /* skip */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [date, session, products]);

  if (!loading && entries.length === 0) return null;

  const readyCount = entries.filter(
    (e) =>
      deriveStatus(e.target, e.log, e.cookableItem?.outputType) === "ready",
  ).length;
  const doneCount = entries.filter((e) =>
    ["ready", "short", "excess"].includes(
      deriveStatus(e.target, e.log, e.cookableItem?.outputType),
    ),
  ).length;

  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            padding: "4px 14px",
            borderRadius: 20,
            background: sc.bg,
            color: sc.color,
            border: `1.5px solid ${sc.border}`,
          }}
        >
          {SESSION_LABEL[session]}
        </span>
        {!loading && (
          <span style={{ fontSize: 13, color: "#888" }}>
            {readyCount}/{entries.length} ready · {doneCount} completed
          </span>
        )}
      </div>

      {loading ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #eee",
            padding: 24,
            textAlign: "center",
          }}
        >
          <Spinner size="sm" />
        </div>
      ) : (
        entries.map((entry, i) => (
          <ItemCard
            key={entry.cookableItem?._id || i}
            entry={entry}
            type={entry.cookableItem?.outputType}
          />
        ))
      )}
    </div>
  );
}

export default function AdminTrackerTab() {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [sessionFilter, setSessionFilter] = useState("all");
  const [products, setProducts] = useState([]);
  const [loadingProds, setLoadingProds] = useState(false);

  useEffect(() => {
    setLoadingProds(true);
    axios
      .get(PRODUCTS_API)
      .then((res) => {
        if (res.data.success) setProducts(res.data.data);
      })
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoadingProds(false));
  }, []);

  const sessions = sessionFilter === "all" ? ["b", "l", "d"] : [sessionFilter];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 2 }}>
            Production Tracker
          </h5>
          <div style={{ fontSize: 13, color: "#888" }}>
            Full cooking log — every batch, shortage and recook
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Form.Control
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ borderRadius: 8, width: "auto" }}
          />
          <Form.Select
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
            style={{ borderRadius: 8, width: "auto" }}
          >
            <option value="all">All Sessions</option>
            <option value="b">Breakfast</option>
            <option value="l">Lunch</option>
            <option value="d">Dinner</option>
          </Form.Select>
        </div>
      </div>

      {loadingProds ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <div
            style={{
              marginBottom: 16,
              fontSize: 13,
              fontWeight: 600,
              color: "#aaa",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {moment(date).format("dddd, MMMM D, YYYY")}
          </div>
          {sessions.map((s) => (
            <SessionBlock
              key={`${date}-${s}`}
              date={date}
              session={s}
              products={products}
            />
          ))}
        </>
      )}
    </>
  );
}
